import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import LoadingSpinner from '@/components/layout/LoadingSpinner'
import { Gift, Star, Crown, Heart, Check } from 'lucide-react'
import ReferralStampHistory from '@/components/customer/ReferralStampHistory'
import RewardDetailModal from '@/components/customer/RewardDetailModal'

interface CustomerData {
  id: string
  visit_stamps: number
  referral_stamps: number
  points: number
  tenant_id: string
}

interface ReferralRewardAssignment {
  id: string
  tenant_id: string
  reward_id: string
  referral_count: number
  is_active: boolean
  reward: {
    id: string
    name: string
    description: string
    reward_type: 'discount' | 'free_item' | 'points' | 'special'
    reward_value: string
    image_url?: string
    admin_approval_required: boolean
  }
}

interface CustomerRedemption {
  id: string
  reward_assignment_id: string
  status: 'pending' | 'approved' | 'rejected'
  redemption_code: string
  redeemed_at: string
}

interface RedemptionResult {
  success?: boolean
  error?: string
  redemption_code?: string
  reward_name?: string
  stamps_used?: number
}

interface RedemptionRPCResult {
  success: boolean
  error?: string
  required?: number
  available?: number
  existing_status?: string
  message?: string
  redemption_id?: string
  status?: string
  admin_approval_required?: boolean
}

const getIcon = (rewardType: string) => {
  switch (rewardType) {
    case 'discount': return Star
    case 'free_item': return Gift
    case 'points': return Crown
    case 'special': return Heart
    default: return Gift
  }
}

const CustomerRewards = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [referralRewards, setReferralRewards] = useState<ReferralRewardAssignment[]>([])
  const [customerRedemptions, setCustomerRedemptions] = useState<CustomerRedemption[]>([])
  const [loading, setLoading] = useState(true)
  const [redeeming, setRedeeming] = useState<string | null>(null)
  const [selectedReward, setSelectedReward] = useState<ReferralRewardAssignment | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (user) {
      fetchCustomerData()
    }
  }, [user])

  useEffect(() => {
    if (customerData) {
      fetchReferralRewards()
      fetchCustomerRedemptions()
    }
  }, [customerData])

  const fetchCustomerData = async () => {
    if (!user?.id) return

    try {
      const fullName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || 'Kunde'
      
      // Use the secure RPC function
      const { data, error } = await supabase.rpc('get_or_create_customer_profile', {
        p_user_id: user.id,
        p_email: user.email || '',
        p_full_name: fullName
      })

      if (error) throw error

      if (data && data.length > 0) {
        const customerRecord = data[0]
        setCustomerData({
          id: customerRecord.id,
          visit_stamps: customerRecord.visit_stamps,
          referral_stamps: customerRecord.referral_stamps,
          points: customerRecord.points,
          tenant_id: customerRecord.tenant_id
        })
      } else {
        throw new Error('Failed to get or create customer profile')
      }
    } catch (error) {
      console.error('Error fetching customer data:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Kundendaten konnten nicht geladen werden.",
      })
    }
  }

  const fetchReferralRewards = async () => {
    if (!customerData?.tenant_id) return

    try {
      const { data, error } = await supabase
        .from('referral_reward_assignments')
        .select(`
          *,
          reward:rewards (
            id,
            name,
            description,
            reward_type,
            reward_value,
            image_url,
            admin_approval_required
          )
        `)
        .eq('tenant_id', customerData.tenant_id)
        .eq('is_active', true)
        .order('referral_count', { ascending: true })

      if (error) throw error
      setReferralRewards((data as ReferralRewardAssignment[]) || [])
    } catch (error) {
      console.error('Error fetching referral rewards:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Empfehlungs-Prämien konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomerRedemptions = async () => {
    if (!customerData?.id) return

    try {
      const { data, error } = await supabase
        .from('customer_reward_redemptions')
        .select('id, reward_assignment_id, status, redemption_code, redeemed_at')
        .eq('customer_id', customerData.id)

      if (error) throw error
      setCustomerRedemptions((data as CustomerRedemption[]) || [])
    } catch (error) {
      console.error('Error fetching customer redemptions:', error)
    }
  }

  const handleRewardClick = (rewardAssignment: ReferralRewardAssignment) => {
    setSelectedReward(rewardAssignment)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedReward(null)
  }

  const handleRedeem = async (rewardAssignment: ReferralRewardAssignment) => {
    if (!customerData) return

    const availableStamps = customerData.referral_stamps
    
    if (availableStamps < rewardAssignment.referral_count) {
      toast({
        variant: "destructive",
        title: "Nicht genügend Empfehlungen",
        description: `Sie benötigen ${rewardAssignment.referral_count} Empfehlungen für diese Belohnung.`,
      })
      return
    }

    setRedeeming(rewardAssignment.id)

    try {
      // Use secure RPC function for redemption
      const { data, error } = await supabase.rpc('create_customer_reward_redemption_secure', {
        p_customer_id: customerData.id,
        p_reward_assignment_id: rewardAssignment.id
      })

      if (error) {
        console.error('RPC call failed:', error)
        throw new Error('Belohnung konnte nicht eingelöst werden. Versuchen Sie es später erneut.')
      }

      const result = data as unknown as RedemptionRPCResult

      if (!result.success) {
        // Handle specific error cases
        switch (result.error) {
          case 'insufficient_referrals':
            throw new Error(`Sie benötigen ${result.required} Empfehlungsstempel, haben aber nur ${result.available}.`)
          case 'already_redeemed_or_pending':
            throw new Error(`Diese Belohnung wurde bereits beantragt (Status: ${result.existing_status}).`)
          case 'tenant_mismatch':
            throw new Error('Die Belohnung ist nicht für Ihr Konto verfügbar.')
          case 'reward_not_found':
          case 'reward_assignment_not_found':
            throw new Error('Die angeforderte Belohnung ist nicht mehr verfügbar.')
          default:
            throw new Error(result.message || 'Ein unbekannter Fehler ist aufgetreten.')
        }
      }

      // Success - show appropriate message
      const statusMessage = result.admin_approval_required
        ? "Ihre Anfrage wurde erfolgreich eingereicht und wartet auf die Genehmigung des Administrators."
        : "Belohnung erfolgreich eingelöst!"

      toast({
        title: "Belohnung beantragt",
        description: statusMessage,
      })
      
      // Refresh redemptions data
      await fetchCustomerRedemptions()
      
      // If no admin approval required, refresh customer data
      if (!result.admin_approval_required) {
        await fetchCustomerData()
      }
      
      // Close modal on successful redemption
      closeModal()
    } catch (error: any) {
      console.error('Redemption error:', error)
      
      const errorMessage = error.message?.includes('verifiziert') || error.message?.includes('Belohnung') 
        ? error.message 
        : "Die Belohnung konnte nicht eingelöst werden. Versuchen Sie es später erneut."
      
      toast({
        variant: "destructive",
        title: "Fehler beim Einlösen",
        description: errorMessage,
      })
    } finally {
      setRedeeming(null)
    }
  }

  const canRedeem = (rewardAssignment: ReferralRewardAssignment) => {
    if (!customerData) return false
    return customerData.referral_stamps >= rewardAssignment.referral_count
  }

  const getRedemptionStatus = (rewardAssignment: ReferralRewardAssignment) => {
    const redemption = customerRedemptions.find(r => r.reward_assignment_id === rewardAssignment.id)
    if (!redemption) return null
    return redemption
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    )
  }

  const sortedRewards = referralRewards.sort((a, b) => a.referral_count - b.referral_count)
  const currentStamps = customerData?.referral_stamps || 0
  
  const getNextReward = () => {
    return sortedRewards.find(reward => currentStamps < reward.referral_count)
  }
  
  const nextReward = getNextReward()

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Hallo {user?.user_metadata?.first_name || user?.email?.split('@')[0] || 'Kunde'},</h1>
          <h2 className="text-xl text-muted-foreground mb-4">willkommen in deinem Prämienprogramm</h2>
          <p className="text-muted-foreground">
            Hier kannst du deine Prämien weiterempfehlen und gleichzeitig profitieren!
          </p>
        </div>

        {/* Rewards Progress Card */}
        {customerData && (
          <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
            <CardHeader>
              <CardTitle className="text-center text-primary mb-4">
                Sichere dir einen zusätzlichen Extra Bonus nach Anzahl erfolgreicher Empfehlungen
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {referralRewards.length > 0 ? (
                <>
                  {/* Progress Bar */}
                  <div className="relative mb-8">
                    {/* Background line */}
                    <div className="absolute top-6 left-0 right-0 h-1 bg-muted rounded-full"></div>
                    
                    {/* Progress line */}
                    <div 
                      className="absolute top-6 left-0 h-1 bg-primary rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min((currentStamps / (sortedRewards[sortedRewards.length - 1]?.referral_count || 1)) * 100, 100)}%`
                      }}
                    ></div>
                    
                    {/* Reward milestones */}
                    <div className="flex justify-between items-start">
                      {sortedRewards.map((rewardAssignment, index) => {
                        const isReached = currentStamps >= rewardAssignment.referral_count
                        const IconComponent = getIcon(rewardAssignment.reward.reward_type)
                        
                        const redemptionStatus = getRedemptionStatus(rewardAssignment)
                        
                        return (
                          <div key={rewardAssignment.id} className="flex flex-col items-center relative">
                            {/* Milestone circle */}
                            <div className={`w-12 h-12 rounded-full border-3 flex items-center justify-center mb-3 transition-all duration-300 ${
                              isReached 
                                ? 'bg-primary border-primary text-primary-foreground' 
                                : 'bg-background border-muted-foreground/30 text-muted-foreground'
                            }`}>
                              {isReached ? (
                                <Check className="h-6 w-6" />
                              ) : (
                                <span className="text-xs font-bold">{rewardAssignment.referral_count}</span>
                              )}
                            </div>
                            
                            {/* Reward image/icon placeholder */}
                            <div 
                              className={`w-16 h-16 rounded-lg border-2 flex items-center justify-center mb-2 transition-all duration-300 cursor-pointer hover:scale-105 ${
                                isReached ? 'border-primary bg-primary/10' : 'border-muted bg-muted/30'
                              }`}
                              onClick={() => handleRewardClick(rewardAssignment)}
                            >
                              {rewardAssignment.reward.image_url ? (
                                <img 
                                  src={rewardAssignment.reward.image_url} 
                                  alt={rewardAssignment.reward.name}
                                  className="w-full h-full object-cover rounded-md"
                                  onError={(e) => {
                                    console.log('🚫 Image load error:', rewardAssignment.reward.image_url)
                                    e.currentTarget.style.display = 'none'
                                  }}
                                />
                              ) : (
                                <IconComponent className={`h-8 w-8 ${isReached ? 'text-primary' : 'text-muted-foreground'}`} />
                              )}
                            </div>
                            
                            {/* Reward name */}
                            <div 
                              className="text-center min-h-[2.5rem] flex items-center cursor-pointer"
                              onClick={() => handleRewardClick(rewardAssignment)}
                            >
                              <span className={`text-xs font-medium px-2 py-1 rounded-full hover:scale-105 transition-transform ${
                                isReached ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                              }`}>
                                {rewardAssignment.reward.name}
                              </span>
                            </div>
                            
                            {/* Status button */}
                            <div className="mt-2">
                              {redemptionStatus ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled
                                  className="bg-muted text-muted-foreground text-xs px-3 py-1"
                                >
                                  Bereits beantragt
                                </Button>
                              ) : isReached ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleRedeem(rewardAssignment)}
                                  disabled={redeeming === rewardAssignment.id}
                                  className="bg-primary/10 text-primary hover:bg-primary/20 text-xs px-3 py-1"
                                >
                                  {redeeming === rewardAssignment.id ? "Wird beantragt..." : "Beantragen"}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="secondary" 
                                  disabled
                                  className="bg-muted text-muted-foreground text-xs px-3 py-1"
                                >
                                  Beantragen
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  
                  {/* Current status */}
                  <div className="text-center">
                    {nextReward ? (
                      <p className="text-sm text-muted-foreground">
                        Nur noch <strong className="text-primary">{nextReward.referral_count - currentStamps} Empfehlungen</strong> bis "{nextReward.reward.name}"
                      </p>
                    ) : (
                      <p className="text-sm text-success font-medium">
                        Herzlichen Glückwunsch! Sie haben alle Prämien erreicht!
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Noch keine Empfehlungsprämien verfügbar.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ihr Administrator wird bald Prämien für Sie einrichten.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Current Stats - Only Visit and Referral Stamps */}
        {customerData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card>
              <CardContent className="flex items-center p-6">
                <Heart className="h-8 w-8 text-primary mr-3" />
                <div>
                  <p className="text-2xl font-bold">{customerData.visit_stamps}</p>
                  <p className="text-sm text-muted-foreground">Besuchsstempel</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center p-6">
                <Gift className="h-8 w-8 text-accent mr-3" />
                <div>
                  <p className="text-2xl font-bold">{customerData.referral_stamps}</p>
                  <p className="text-sm text-muted-foreground">Empfehlungsstempel</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Referral Stamp History */}
        {customerData && (
          <Card>
            <CardHeader>
              <CardTitle>Empfehlungshistorie</CardTitle>
              <CardDescription>
                Ihre letzten Empfehlungsstempel im Überblick
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ReferralStampHistory customerId={customerData.id} tenantId={customerData.tenant_id} />
            </CardContent>
          </Card>
        )}

        {/* Reward Detail Modal */}
        <RewardDetailModal
          isOpen={isModalOpen}
          onClose={closeModal}
          rewardAssignment={selectedReward}
          canRedeem={selectedReward ? canRedeem(selectedReward) : false}
          redemptionStatus={selectedReward ? getRedemptionStatus(selectedReward) : null}
          onRedeem={handleRedeem}
          redeeming={redeeming === selectedReward?.id}
          currentStamps={currentStamps}
        />
      </div>
    </DashboardLayout>
  )
}

export default CustomerRewards